/**
 * P0-DISCOVERY-01 — Global non-blocking Victory Card prompt.
 *
 * Listens for `vs:victory:prompt` events fired from PremiumGoal,
 * NationOvertake, PredictionCard (correct vote), DailyWow and
 * TopFanWeekly. Shows an expanded card for 8s with three CTAs
 * (WhatsApp · Compartir · Ver mi tarjeta), then collapses into a
 * compact pill that the user can tap to expand again or dismiss.
 *
 * No engines, no schema, no auto-route, no auto-share.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Share2, Sparkles, X } from "lucide-react";
import {
  subscribeVictoryPrompt,
  type VictoryPromptDetail,
} from "@/lib/viral/victoryPromptBus";
import { openShareSheet } from "@/components/viral/ShareSheetHost";
import { trackEvent } from "@/lib/audit";
import { buildCanonicalShareUrl } from "@/lib/share/canonicalShare";

const EXPAND_MS = 8_000;

const COPY: Record<
  VictoryPromptDetail["source"],
  { headline: string; subline: string }
> = {
  prediction_correct: {
    headline: "🏆 Acertaste tu predicción",
    subline: "Compártelo y suma puntos para tu nación",
  },
  nation_overtake: {
    headline: "🚀 Tu nación subió en el ranking",
    subline: "Comparte el momento antes que vuelva a moverse",
  },
  premium_goal: {
    headline: "⚽ ¡GOL épico!",
    subline: "Conviértelo en tarjeta y compártelo en 1 toque",
  },
  top_fan_weekly: {
    headline: "🌟 Eres Top Fan de la semana",
    subline: "Comparte tu logro y reta a tus amigos",
  },
  daily_wow: {
    headline: "🎯 Tu momento del día está listo",
    subline: "Compártelo antes de que termine el día",
  },
};

export function VictoryCardPrompt() {
  const [detail, setDetail] = useState<VictoryPromptDetail | null>(null);
  const [expanded, setExpanded] = useState(false);
  const collapseTimer = useRef<number | null>(null);

  useEffect(() => {
    return subscribeVictoryPrompt((d) => {
      setDetail(d);
      setExpanded(true);
      if (collapseTimer.current) window.clearTimeout(collapseTimer.current);
      collapseTimer.current = window.setTimeout(() => {
        setExpanded(false);
        void trackEvent("victory_card_prompt_collapsed", { source: d.source });
      }, EXPAND_MS);
    });
  }, []);

  useEffect(
    () => () => {
      if (collapseTimer.current) window.clearTimeout(collapseTimer.current);
    },
    [],
  );

  if (!detail) return null;
  const copy = {
    headline: detail.headline ?? COPY[detail.source].headline,
    subline: detail.subline ?? COPY[detail.source].subline,
  };

  const dismiss = () => {
    setDetail(null);
    setExpanded(false);
  };

  const onAction = (action: "whatsapp" | "share" | "view") => {
    void trackEvent("victory_card_prompt_action", {
      source: detail.source,
      action,
    });
    if (action === "whatsapp") {
      const url = buildCanonicalShareUrl("/viral-cards", {
        source: "victory_prompt",
        medium: "whatsapp",
        campaign: detail.source,
      });
      const msg = `${copy.headline} — ${copy.subline} ${url}`;
      const wa = `https://wa.me/?text=${encodeURIComponent(msg)}`;
      try {
        window.open(wa, "_blank", "noopener,noreferrer");
      } catch {
        openShareSheet();
      }
    } else if (action === "share") {
      openShareSheet();
    }
    // 'view' → handled by <Link>
  };

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => {
          setExpanded(true);
          if (collapseTimer.current) window.clearTimeout(collapseTimer.current);
          collapseTimer.current = window.setTimeout(() => setExpanded(false), EXPAND_MS);
        }}
        className="pointer-events-auto fixed left-1/2 z-[70] flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-primary/50 bg-background/90 px-3 py-1 text-[11px] font-black shadow-lg backdrop-blur hover:bg-background"
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 150px)" }}
        aria-label="Compartir mi tarjeta"
      >
        <Sparkles className="h-3 w-3 text-primary" />
        🏆 Comparte
      </button>
    );
  }

  return (
    <div
      role="dialog"
      aria-label="Comparte tu resultado"
      className="pointer-events-auto fixed left-1/2 z-[70] w-[min(94vw,360px)] -translate-x-1/2 rounded-2xl border border-primary/50 bg-background/95 p-3 shadow-2xl backdrop-blur-md animate-fade-in"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 150px)" }}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[9px] font-black uppercase tracking-widest text-primary">
            🏆 Comparte tu resultado
          </div>
          <div className="truncate text-sm font-black leading-tight">
            {copy.headline}
          </div>
          <div className="mt-0.5 truncate text-[10px] text-muted-foreground">
            {copy.subline}
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Cerrar"
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-1.5">
        <button
          type="button"
          onClick={() => onAction("whatsapp")}
          className="rounded-lg bg-[oklch(0.7_0.18_145)] px-2 py-1.5 text-[11px] font-black text-background"
        >
          💬 WhatsApp
        </button>
        <button
          type="button"
          onClick={() => onAction("share")}
          className="flex items-center justify-center gap-1 rounded-lg border border-border bg-background px-2 py-1.5 text-[11px] font-black"
        >
          <Share2 className="h-3 w-3" /> Compartir
        </button>
        <Link
          to="/viral-cards"
          onClick={() => onAction("view")}
          className="flex items-center justify-center rounded-lg bg-primary px-2 py-1.5 text-center text-[11px] font-black text-primary-foreground"
        >
          Ver mi tarjeta
        </Link>
      </div>
    </div>
  );
}
